<template>

  <m-pay-layout>

    <template slot="links">

      <router-link :to="{ name: 'order', params: { orderId: credit.orderId } }">
        <v-btn color="secondary" flat small>
          Order
          <v-icon class="ml-1" dark right small>create</v-icon>
        </v-btn>
      </router-link>
      <router-link :to="{ name: 'consumer-credits', query: { f_customerId: credit.customerId } }">
        <v-btn color="secondary" flat small>
          Customer Loans
          <v-icon class="ml-1" dark right small>euro</v-icon>
        </v-btn>
      </router-link>
      <router-link v-if="credit.merchantId"
                   :to="{ name: 'merchant', params: { merchantId: credit.merchantId } }">
        <v-btn color="secondary" flat small>
          Merchant
          <v-icon class="ml-1" dark right small>store</v-icon>
        </v-btn>
      </router-link>
      <router-link v-if="hasAdminRole"
                   :to="{ name: 'customer', params: { customerId: credit.customerId } }">
        <v-btn color="secondary" flat small>
          Customer
          <v-icon class="ml-1" dark right small>person</v-icon>
        </v-btn>
      </router-link>

      <router-link v-if="credit.allOfferCount > 0"
                   :to="{ name: 'credit-offers', query: { f_consumerCreditId: credit.id } }">
        <v-btn color="secondary" flat small>
          Offers
          <v-icon class="ml-1" dark right small>list_alt</v-icon>
        </v-btn>
      </router-link>

      <router-link v-if="credit.allAuthenticationCount > 0" :to="{ name: 'credit-authentications',
            query: { f_orderId: credit.orderId } }">
        <v-btn color="secondary" flat small>
          Authentications
          <v-icon class="ml-1" dark right small>phonelink_lock</v-icon>
        </v-btn>
      </router-link>

      <router-link :to="{ name: 'credit-signatures',
            query: { f_consumerCreditId: credit.id } }">
        <v-btn color="secondary" flat small>
          Signatures
          <v-icon class="ml-1" dark right small>done_all</v-icon>
        </v-btn>
      </router-link>


    </template>

    <v-layout :column="$vuetify.breakpoint.smAndDown">

      <v-flex class="mx-2">
        <m-pay-generic-viewer :bean="credit"
                              :beanProperties="viewableProperties"
                              :formatters="tableFormatters"
                              :roles="fieldRoles"
                              :statusClasses="consumerCreditStatusClasses"
                              class="mb-2"
                              title="Loan Details"
        >
        </m-pay-generic-viewer>

        <v-data-iterator :items="credit.applications"
                         :rows-per-page-items="rowsPerPageItems"
                         :hide-actions="hideActions"
                         :pagination.sync="pagination">
          <template #item="{ item }">
              <m-pay-generic-viewer :bean="item"
                                    :beanProperties="[
                                      'id',
                                      'externalId',
                                      'amount',
                                      'agreementSigningDate',
                                      'deviceOS',
                                      'downPaymentType',
                                      'incomeType',
                                      'isActive']"
                                    :formatters="tableFormatters"
                                    :roles="fieldRoles"
                                    :statusClasses="consumerCreditStatusClasses"
                                    class="mb-2"
                                    title="Application Details"
          >
          </m-pay-generic-viewer>
          </template>
        </v-data-iterator>

        <m-pay-generic-viewer
          :bean="credit.chosenOffer"
          :beanProperties="[
            'financingInstitutionName',
            'productName',
            'principalAmount',
            'downPaymentAmount',
            'id'
          ]"
          :collapsed="true"
          class="mb-2"
          title="Chosen Offer"
        />

        <m-pay-generic-viewer v-if="hasAdminRole"
                              :bean="credit.signature"
                              :beanProperties="[
                                'signingServiceId',
                                'status',
                                'signedObjectId',
                                'externalId',
                                'error']"
                              :collapsed="true"
                              :formatters="tableFormatters"
                              class="mb-2"
                              title="Signature"
        >
        </m-pay-generic-viewer>
      </v-flex>

      <v-flex v-if="hasAdminRole" class="mr-2" grow>
        <m-pay-generic-viewer
          :bean="credit.person"
          :beanProperties="['personalId', 'firstName', 'lastName', 'dateOfBirth',
            'phoneNumber', 'email']"
          :formatters="tableFormatters"
          class="mb-2"
          title="Person"
        >
        </m-pay-generic-viewer>
        <v-card v-if="hasAdminRole" class="mb-3">
          <m-pay-generic-editor
            :loading="loading"
            :bean="credit"
            :beanProperties="commentsEditableProperties"
            :validationRules="commentsEditableRules"
            :beanPropertyValues="commentsBeanPropertyValues"
            :clearableProperties="commentsClearableProperties"
            :inputTypes="commentsInputTypes"
            @submit="editComment"
          />
          <m-pay-generic-viewer
            :bean-properties="['responsibleSupportPerson']"
            :bean="credit"
          />
          <v-btn :color="'primary'"
                 :disabled="loading"
                 :loading="loading"
                 flat
                 @click.native="setResponsiblePerson">
            <slot name="submitButton">
              Set me
              <v-icon right dark>done</v-icon>
            </slot>
          </v-btn>
        </v-card>
      </v-flex>
      <v-flex v-if="allowRefund || allowMarkAsConfirmed" class="mr-2" grow>
        <v-card class="mb-4">
          <v-card-title>
            <span class="headline">Loan actions</span>
          </v-card-title>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              v-if="allowMarkAsConfirmed"
              :disabled="loading" :loading="loading" color="secondary"
              flat
              large
              @click.native="markAsConfirmed"
            >
              Mark as confirmed
              <v-icon dark right>done</v-icon>
            </v-btn>
            <v-btn
              v-if="allowRefund"
              :disabled="loading" :loading="loading" color="error darken-1"
              flat
              large
              @click.native="showRefundDialog = true"
            >
              Refund
              <v-icon dark right>restore</v-icon>
            </v-btn>
            <m-pay-confirm-dialog
              :show="showConfirmationDialog"
              :title="confirmationDialog.description"
              @yes="confirmationDialog.action"
              @cancel="hideDialog"
              maxWidth="700px"
            />
          </v-card-actions>
        </v-card>
      </v-flex>

      <m-pay-consumer-credit-refund
        :consumerCredit="credit"
        :show.sync="showRefundDialog"
        @refund="refundConsumerCredit"
      ></m-pay-consumer-credit-refund>
    </v-layout>
  </m-pay-layout>

</template>

<script>
import axios from 'axios';
import queries from '../utils/queries';
import roles from '../utils/user-roles';
import { consumerCreditStatusClasses } from '../utils/statuses';
import formatters from '../utils/formatters';
import MPayGenericViewer from './MPayGenericViewer';
import MPayConsumerCreditRefund from './MPayConsumerCreditRefund';
import MPayGenericEditor from './MPayGenericEditor';
import MPayConfirmDialog from './MPayConfirmDialog';

export default {
  name: 'MPayConsumerCreditDetailsView',

  components: {
    MPayGenericEditor,
    MPayGenericViewer,
    MPayConsumerCreditRefund,
    MPayConfirmDialog,
  },

  data: () => ({
    loading: false,
    credit: {},
    viewableProperties: [
      'id',
      'status',
      'failureReason',
      'totalRefundAmount',
      'loanNumber',
      'applicationExternalId',
      'chosenOffer.financingInstitutionName',
      'offerRetrievalFinished',
      'createdDate',
      'updatedDate',
      'graceRepaymentCount',
      'downPaymentAmount',
      'downPaymentPurchaseId',
      'applicationUrl',
      'merchantOrderNumber',
    ],
    consumerCreditStatusClasses,
    formatters,
    tableFormatters: {
      totalAmount: formatters.amount,
      totalRefundAmount: formatters.amount,
      createdDate: formatters.dateTime,
      updatedDate: formatters.dateTime,
      agreementSigningDate: formatters.date,
    },
    showRefundDialog: false,
    showConfirmationDialog: false,
    confirmationDialog: {
      action: () => {
      },
      title: '',
      description: '',
    },
    rowsPerPageItems: [1],
    pagination: {
      rowsPerPage: 1,
    },
    commentsEditableProperties: [
      'callTheme',
      'comment',
    ],
    commentsEditableRules: [
      'max:50',
      'max:511',
    ],
    commentsClearableProperties: [
      'callTheme',
    ],
  }),

  computed: {
    allowRefund() {
      return this.credit.status === 'CONFIRMED' || this.credit.status === 'REFUNDED';
    },
    hasAdminRole() {
      return this.$store.getters.hasAnyRole(roles.READ_ANY_USER);
    },
    allowMarkAsConfirmed() {
      return this.credit.status === 'AGREEMENT_DOCUMENT_SIGNED'
        || this.credit.status === 'DOWN_PAYMENT_INITIATED'
        || this.credit.status === 'DOWN_PAYMENT_RECEIVED'
        || this.credit.status === 'WAITING_FOR_CONFIRMATION'
        || this.credit.status === 'FAILED';
    },
    hideActions() {
      return this.credit.applications && this.credit.applications.length <= 1;
    },
    commentsBeanPropertyValues() {
      return {
        callTheme: ['EMPLOYEE_TESTS', 'NO_ANSWER', 'CHANGED_MIND', 'APPLICATION_FORM_ISSUES', 'DECLINED_BY_LENDERS',
          'INFO_ON_FREE_LOAN_OFFERS', 'INFO_ON_OTHER_LOAN_OFFERS', 'INFO_ON_OPEN_CONTRACTS', 'SIGNING_ISSUES',
          'DOWN_PAYMENT_ISSUES', 'LENDING_DOWNTIME', 'CHANGED_MIND_OR_LENDING_CONDITIONS'],
      };
    },
    commentsInputTypes() {
      return {
        comment: 'textarea',
      };
    },
  },

  methods: {
    fetchConsumerCredit() {
      this.loading = true;
      axios.get(queries.consumerCredit(this.$route.params.consumerCreditId))
        .then((response) => {
          this.credit = response.data;
          this.loading = false;
        })
        .catch((e) => {
          this.credit = {};
          this.showError(e);
          this.loading = false;
        });
    },
    refundConsumerCredit(model) {
      this.loading = true;
      axios.post(queries.consumerCreditRefundRequests(this.credit.id), model)
        .then(() => {
          this.loading = false;
          this.showSuccessMessage('Credit refund request has been sent to financing institution');
          this.fetchConsumerCredit();
        })
        .catch((e) => {
          this.showError(e);
          this.loading = false;
        });
    },
    editComment(model) {
      this.loading = true;
      axios.post(queries.editConsumerCreditComment(this.credit.id), model)
        .then(() => {
          this.loading = false;
          this.showSuccessMessage('Consumer credit comment successfully modified');
          this.fetchConsumerCredit();
        })
        .catch((e) => {
          this.showError(e);
          this.loading = false;
        });
    },
    setResponsiblePerson() {
      this.loading = true;
      axios.put(queries.setConsumerCreditResponsiblePerson(this.credit.id))
        .then(() => {
          this.loading = false;
          this.showSuccessMessage('Consumer credit comment successfully modified');
          this.fetchConsumerCredit();
        })
        .catch((e) => {
          this.showError(e);
          this.loading = false;
        });
    },
    markAsConfirmed() {
      this.showDialog('Do you really want to mark this consumer credit as confirmed?', () => {
        this.hideDialog();
        this.loading = true;
        axios.post(queries.confirmConsumerCredit(this.credit.id))
          .then(() => {
            this.loading = false;
            this.showSuccessMessage('Consumer credit marked as confirmed');
            this.fetchConsumerCredit();
          })
          .catch((e) => {
            this.showError(e);
            this.loading = false;
          });
      });
    },
    showDialog(message, onSuccessCallback) {
      this.showConfirmationDialog = true;
      this.confirmationDialog = {
        title: '',
        description: message,
        action: onSuccessCallback,
      };
    },
    hideDialog() {
      this.showConfirmationDialog = false;
    },
  },

  mounted() {
    this.fetchConsumerCredit();
  },

};
</script>

<style>
.v-card__actions a {
  text-decoration: none;
}
</style>



// WEBPACK FOOTER //
// src/components/MPayConsumerCreditView.vue